import { NgForOf, NgIf, NgStyle } from "@angular/common";
import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, HostListener, Input, OnDestroy, OnInit, ChangeDetectionStrategy, ViewChild, ElementRef } from '@angular/core';
import { PxTooltipDirective } from '../../../centrui/src/app/directives/px-tooltip/px-tooltip.directive';
import { simpleFadeAnimation } from "../../animations";
import {
  CentruiButtonIconComponent
} from "../../../centrui/src/app/components/centrui-button-icon/centrui-button-icon.component";
import { ViewerDirective } from "../../../centrui/src/app/directives/viewer.directive";
import { SafeHtml } from "../../../centrui/src/app/pipes/safe.pipe";
import { Referenceable } from "../../interfaces/Referenceable";
import { ItemContentAttachment } from "../../models/ItemContentAttachment";
import { Project } from "../../models/Project";
import { Reference } from "../../models/Reference";
import { ReferenceService } from "../../services/reference.service";
import { LightboxService } from "../../services/lightbox.service";
import { Attachment } from "../../models/Attachment";

@Component({
  selector: 'app-image-viewer',
  standalone: true,
  imports: [
    CentruiButtonIconComponent,
    PxTooltipDirective,
    NgIf,
    NgForOf,
    NgStyle,
    SafeHtml
  ],
  templateUrl: './image-viewer.component.html',
  styleUrl: './image-viewer.component.scss',
  animations: [
    simpleFadeAnimation
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    ViewerDirective
  ]
})
export class ImageViewerComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
  @Input() referenceable!: Attachment;
  @ViewChild('closeButton') private closeButton!: ElementRef;
  @ViewChild('viewer') private viewerRef!: ElementRef;

  showReferences = false;
  showContext = false;
  positionSet = false;
  position!: { top: number; right: number };
  gallery!: any;
  galleryHidden: boolean = false;
  resizeListener: () => void;

  @HostListener('document:keydown.escape', ['$event'])
  onKeyUp(_event: Event) {
    this.close();
  }

  constructor(
    private referenceService: ReferenceService,
    private lightboxService: LightboxService,
    private viewerDirective: ViewerDirective,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.referenceService.$reference.subscribe(reference => {
      if(reference && this.gallery) {
        this.showReferences = false;
        this.showContext = false;
        this.toggleGalleryView();
      }
      if(!reference && this.galleryHidden) {
        this.toggleGalleryView();
      }
    })
    this.resizeListener = () => {
      this.setPosition();
    };
  }

  ngAfterViewChecked(): void {
    const slides = document.getElementsByClassName('gslide-image');
    if (!this.positionSet && slides.length > 0) {
      this.positionSet = true;
      this.setPosition();
    }
  }

  ngAfterViewInit(): void {
    window.addEventListener('resize', this.resizeListener);
  }

  ngOnInit(): void {
    this.openGallery();
  }

  openGallery() {
    if(this.referenceable.file) {
      this.gallery = this.viewerDirective.openGallery(this.referenceable.file, 'image-viewer', {
        closeButton: false,
        onClose: () => this.lightboxService.close()
      });
    }
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.resizeListener);
    this.gallery?.close();
  }

  toggleReferences() {
    this.showReferences = !this.showReferences;
  }

  toggleContext() {
    this.showContext = !this.showContext;
  }

  hidePanel() {
    this.showReferences = false;
    this.showContext = false;
  }

  toggleGalleryView() {
    const lightbox = document.getElementById('glightbox-body');
    if(this.galleryHidden) {
      lightbox!.style.opacity = '100';
      lightbox!.style.pointerEvents = 'auto';
      this.viewerRef.nativeElement.style.opacity = '100';
      this.viewerRef.nativeElement.style.pointerEvents = 'auto';
      this.galleryHidden = false;
    } else {
      lightbox!.style.opacity = '0';
      lightbox!.style.pointerEvents = 'none';
      this.viewerRef.nativeElement.style.opacity = '0';
      this.viewerRef.nativeElement.style.pointerEvents = 'none';
      this.galleryHidden = true;
    }
  }

  viewReference(reference: Reference) {
    this.referenceService.viewReference(reference, Project, {id: 1} as any, false);
  }

  close() {
    this.gallery?.close();
  }

  setPosition() {
    const slides = document.getElementsByClassName('gslide-image');
    const slide = slides[0];
    if (!slide) return;

    const img = slide.querySelector('img');
    if (!img) return;

    if (img.complete) {
      const boundingRect = slide.getBoundingClientRect();
      this.position = { top: boundingRect.top, right: boundingRect.right };
      this.changeDetectorRef.detectChanges();
    } else {
      img.addEventListener('load', () => {
        const boundingRect = slide.getBoundingClientRect();
        this.position = { top: boundingRect.top, right: boundingRect.right };
        this.changeDetectorRef.detectChanges();
      });
    }
  }
}
